import { FormikProps } from 'formik';
import { Dropdown, Input, PhoneInput, TextArea } from '../../../components/common/Form';
import Button from '../../../components/common/Button';
import { useCuisinesContext } from '../../../contexts/CuisinesContext';
import { handleHttpsInput } from '../../../utils/general';
import InfoTooltip from '../../../components/common/InfoTooltip/InfoTooltip';
import { RestaurantWizardFormValues } from './restaurantWizardSchema';

const REQUIRED_FIELDS = ['name', 'phone', 'email'] as const;

interface RestaurantBasicInfoPageProps {
  formik: FormikProps<RestaurantWizardFormValues>;
  onNext: () => void;
}

const RestaurantBasicInfoPage = ({ formik, onNext }: RestaurantBasicInfoPageProps) => {
  const { cuisines } = useCuisinesContext();

  const handleNextClicked = () => {
    formik.setTouched(Object.fromEntries(REQUIRED_FIELDS.map((f) => [f, true])), true);
    if (REQUIRED_FIELDS.every((f) => !!formik.values[f] && !formik.errors[f])) {
      onNext();
    }
  };

  return (
    <div className="restaurant-basic-info-page">
      <Input
        name="name"
        label="RESTAURANT NAME"
        className="restaurant-wizard-name-input"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.name}
        touched={formik.touched.name}
        error={formik.errors.name}
      />
      <Input
        name="primaryTagline"
        label={
          <span className="flex items-center gap-1">
            PRIMARY TAGLINE <i>(Optional)</i>
            <InfoTooltip description="A short headline shown on your restaurant's profile page." />
          </span>
        }
        className="restaurant-wizard-primary-tagline-input"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.primaryTagline}
        touched={formik.touched.primaryTagline}
        error={formik.errors.primaryTagline}
      />
      <Input
        name="secondaryTagline"
        label={
          <span className="flex items-center gap-1">
            SECONDARY TAGLINE <i>(Optional)</i>
            <InfoTooltip description="A supporting phrase that complements your primary tagline." />
          </span>
        }
        className="restaurant-wizard-secondary-tagline-input"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.secondaryTagline}
        touched={formik.touched.secondaryTagline}
        error={formik.errors.secondaryTagline}
      />
      <TextArea
        name="description"
        label={
          <span>
            DESCRIPTION <i>(Optional)</i>
          </span>
        }
        className="restaurant-wizard-description-input"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.description}
        touched={formik.touched.description}
        error={formik.errors.description}
      />
      <PhoneInput
        name="phone"
        label="PHONE NUMBER"
        className="restaurant-wizard-phone-input"
        onBlur={formik.handleBlur}
        touched={formik.touched.phone}
        onChange={(phoneNumber: string) => formik.setFieldValue('phone', phoneNumber)}
        value={formik.values.phone}
        error={formik.errors?.phone}
      />
      <Input
        name="email"
        label="EMAIL ADDRESS"
        type="email"
        className="restaurant-wizard-email-input"
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.email}
        touched={formik.touched.email}
        error={formik.errors.email}
      />
      {cuisines?.length > 0 && (
        <Dropdown
          label="CUISINE"
          className="restaurant-wizard-cuisine-select"
          onChange={(value: string) => formik.setFieldValue('cuisine', parseInt(value, 10))}
          value={formik.values.cuisine}
          values={cuisines?.map((cuisine) => ({
            label: cuisine.name,
            value: cuisine.cuisineID
          }))}
        />
      )}
      <Input
        name="website"
        label={
          <span>
            WEBSITE <i>(Optional)</i>
          </span>
        }
        className="restaurant-wizard-website-input"
        onChange={(event: any) => formik.setFieldValue('website', handleHttpsInput(event.target.value))}
        onBlur={formik.handleBlur}
        value={formik.values.website}
        touched={formik.touched.website}
        error={formik.errors.website}
      />
      <Button className="restaurant-wizard-info-next-button" onClick={handleNextClicked}>
        NEXT
      </Button>
    </div>
  );
};

export default RestaurantBasicInfoPage;
